/**
 * FamilyCareNeeds — family-facing UI for the JCC-native care needs
 * workflow. List of your own posts + inline editor / status actions.
 * Available to all Free Family and Family Circle members. Route mounted
 * at /family/care-needs behind HandoverGate accepting family_free OR
 * family_circle.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { PageHero } from "../components/shared/Primitives";
import { PrivacyReassuranceNote } from "../components/shared/PrivacyReassuranceNote";

const CLOSE_OUTCOMES = [
  ["found_via_jcc", "Found a carer through JCC"],
  ["found_elsewhere", "Found a carer elsewhere"],
  ["no_longer_looking", "No longer looking"],
  ["prefer_not_to_say", "Prefer not to say"],
];

const CARE_TYPES = [
  ["live_in", "Live-in"], ["hourly", "Hourly visits"],
  ["waking_night", "Waking nights"], ["sleeping_night", "Sleeping nights"],
  ["respite", "Respite"], ["other", "Other"],
];
const START_TIMINGS = [
  ["asap", "As soon as possible"], ["within_1_month", "Within 1 month"],
  ["1_to_3_months", "1–3 months"], ["3_to_6_months", "3–6 months"],
  ["flexible", "Flexible"], ["exact_date", "On an exact date"],
];
const DURATION_BANDS = [
  ["one_off", "One-off"], ["under_1_month", "Under 1 month"],
  ["1_to_3_months", "1–3 months"], ["3_to_6_months", "3–6 months"],
  ["6_to_12_months", "6–12 months"], ["ongoing", "Ongoing"],
];
const HOURS_BANDS = [
  ["under_10", "Under 10 h/wk"], ["10_to_20", "10–20 h/wk"],
  ["20_to_40", "20–40 h/wk"], ["40_to_60", "40–60 h/wk"],
  ["60_plus", "60+ h/wk"], ["live_in", "Live-in"],
];
const BUDGET_BANDS = [
  ["under_15_per_hour", "Under £15 /h"], ["15_to_20_per_hour", "£15–£20 /h"],
  ["20_to_25_per_hour", "£20–£25 /h"], ["25_plus_per_hour", "£25+ /h"],
  ["under_150_per_day", "Under £150 /day"], ["150_to_200_per_day", "£150–£200 /day"],
  ["200_to_250_per_day", "£200–£250 /day"], ["250_plus_per_day", "£250+ /day"],
  ["flexible", "Flexible / open"],
];
const FUNDING_TYPES = [
  ["private", "Private / self-funded"], ["direct_payment", "Direct payment"],
  ["la_nhs", "Local authority or NHS-related"], ["unsure", "Unsure"],
];
const ESSENTIALS = [
  ["driver_required", "Carer must be a driver"],
  ["own_car_required", "Carer needs own car"],
  ["pet_friendly", "Pet-friendly household"],
  ["no_pets_household", "No pets in household"],
  ["smoker_friendly", "Smoker-friendly"],
  ["non_smoking_household", "Non-smoking household"],
  ["moving_and_handling", "Moving and handling"],
  ["waking_night_required", "Waking nights required"],
  ["dementia_experience", "Dementia experience"],
  ["end_of_life_experience", "End-of-life experience"],
  ["hoisting_experience", "Hoisting experience"],
  ["medication_administration", "Medication administration"],
  ["female_carer_only", "Female carer only"],
  ["male_carer_only", "Male carer only"],
  ["second_language_required", "Second language needed"],
];

const emptyForm = {
  care_recipient_reference: "", relationship_to_recipient: "",
  location_area: "", care_type: "", preferred_start_timing: "",
  start_date: "", duration_band: "", hours_per_week_band: "",
  rota_pattern: "", support_needs: "", night_expectations: "",
  essential_requirements: [], funding_type: "", budget_band: "",
  additional_details: "", expiry_review_date: "",
};

function Sel({ form, set, k, opts, label }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <select value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
              data-testid={`cn-${k}`}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2">
        <option value="">— Select —</option>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function Txt({ form, set, k, label, ...rest }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
             data-testid={`cn-${k}`}
             className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" {...rest}/>
    </label>
  );
}

function Area({ form, set, k, label, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <textarea value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
                rows={rows} data-testid={`cn-${k}`}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"/>
    </label>
  );
}

function StatusBadge({ label, status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700",
    pending_review: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    paused: "bg-slate-100 text-slate-700",
    matched: "bg-teal-100 text-teal-800",
    closed: "bg-slate-100 text-slate-500",
    rejected: "bg-red-100 text-red-800",
    changes_requested: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-slate-100"}`}
          data-testid={`cn-status-${status}`}>
      {label}
    </span>
  );
}

const INTEREST_STATUS_LABELS = {
  submitted: "Awaiting your response",
  accepted: "Introduction accepted",
  declined: "Not progressed",
};
const INTEREST_STATUS_STYLES = {
  submitted: "bg-purple-100 text-purple-800",
  accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-slate-100 text-slate-500",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function CarerProfilePreview({ postId, interestId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/care-needs/${postId}/interests/${interestId}/profile`);
        if (alive) setProfile(data);
      } catch { if (alive) setError(true); }
    })();
    return () => { alive = false; };
  }, [postId, interestId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="carer-profile-preview-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="carer-profile-preview-modal">
        {error ? (
          <p className="text-ink-muted" data-testid="carer-profile-preview-error">This carer's profile isn't available to view yet.</p>
        ) : !profile ? (
          <p className="text-ink-muted">Loading…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3">
              {profile.photo_data_url ? (
                <img src={profile.photo_data_url} alt="" className="w-16 h-16 rounded-full object-cover border border-slate-200" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-ink-muted text-xs">No photo</div>
              )}
              <div>
                <div className="font-display text-lg font-semibold" data-testid="carer-profile-preview-name">{profile.name}</div>
                {profile.general_location && <div className="text-ink-muted text-xs">{profile.general_location}</div>}
              </div>
            </div>
            {profile.about_you && <div><span className="text-ink-muted text-xs block mb-1">About</span><p className="whitespace-pre-wrap">{profile.about_you}</p></div>}
            {(profile.care_types || []).length > 0 && (
              <div>
                <span className="text-ink-muted text-xs block mb-1">Care types</span>
                <div className="flex flex-wrap gap-1.5">{profile.care_types.map((t) => <span key={t} className="pill bg-slate-100 text-ink text-xs">{t}</span>)}</div>
              </div>
            )}
            {profile.care_experience_description && <div><span className="text-ink-muted text-xs block mb-1">Experience</span><p className="whitespace-pre-wrap">{profile.care_experience_description}</p></div>}
            <div className="grid grid-cols-2 gap-3 border-y border-slate-100 py-3">
              <div><span className="text-ink-muted text-xs block">Willing to travel</span>{profile.willing_to_travel || "—"}</div>
              <div><span className="text-ink-muted text-xs block">Earliest start</span>{profile.earliest_start_date || "—"}</div>
              <div><span className="text-ink-muted text-xs block">DBS status</span>{profile.dbs_status || "—"}</div>
              <div><span className="text-ink-muted text-xs block">Insurance</span>{profile.insurance_status || "—"}</div>
            </div>
            {profile.testimonials && <div><span className="text-ink-muted text-xs block mb-1">Testimonials</span><p className="whitespace-pre-wrap">{profile.testimonials}</p></div>}
          </div>
        )}
        <button onClick={onClose} className="mt-5 btn-outline text-sm w-full" data-testid="carer-profile-preview-close">Close</button>
      </div>
    </div>
  );
}

function InterestsPanel({ postId }) {
  const [interests, setInterests] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [profileFor, setProfileFor] = useState(null); // interest_id | null
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/care-needs/${postId}/interests`);
      setInterests(data.interests || []);
    } catch (e) { toast.error("Could not load responses."); }
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  const respond = async (interestId, action) => {
    setBusyId(interestId);
    try {
      await api.post(`/api/care-needs/${postId}/interests/${interestId}/${action}`);
      toast.success(action === "accept" ? "Introduction accepted." : "Marked as not progressed.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Action failed."); }
    finally { setBusyId(null); }
  };

  const inviteToRoom = async (interestId) => {
    try {
      const { data } = await api.get(`/api/care-needs/${postId}/interests/${interestId}/care-room-prefill`);
      const params = new URLSearchParams({
        carer_email: data.carer_email || "", carer_name: data.carer_name || "",
        client_label: data.client_label_suggestion || "",
      });
      if (data.placement_id) params.set("placement_id", data.placement_id);
      navigate(`/care-discussion-room/new?${params.toString()}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not prepare Care Room invite."); }
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`interests-panel-${postId}`}>
      {interests === null ? (
        <p className="text-sm text-ink-muted">Loading responses…</p>
      ) : interests.length === 0 ? (
        <p className="text-sm text-ink-muted">No responses yet.</p>
      ) : (
        <ul className="space-y-3">
          {interests.map((i) => (
            <li key={i.interest_id} className="bg-white rounded-lg border border-slate-200 p-3.5" data-testid={`interest-row-${i.interest_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-sm">{i.carer_name}</span>
                <span className={`pill text-xs ${INTEREST_STATUS_STYLES[i.status] || "bg-slate-100"}`} data-testid={`interest-status-${i.interest_id}`}>
                  {INTEREST_STATUS_LABELS[i.status] || i.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Interested {fmtDateTime(i.created_at)}{i.responded_at ? ` · Responded ${fmtDateTime(i.responded_at)}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {i.profile_available && (
                  <Button size="sm" variant="outline" onClick={() => setProfileFor(i.interest_id)} data-testid={`interest-view-profile-${i.interest_id}`}>
                    View profile
                  </Button>
                )}
                {!i.profile_available && i.status === "submitted" && (
                  <span className="text-xs text-ink-muted italic self-center">No approved profile to view yet</span>
                )}
                {i.status === "submitted" && (
                  <>
                    <Button size="sm" disabled={busyId === i.interest_id} onClick={() => respond(i.interest_id, "accept")} data-testid={`interest-accept-${i.interest_id}`}>
                      Accept interest
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busyId === i.interest_id} onClick={() => respond(i.interest_id, "decline")} data-testid={`interest-decline-${i.interest_id}`}>
                      Decline interest
                    </Button>
                  </>
                )}
                {i.status === "accepted" && (
                  <Button size="sm" onClick={() => inviteToRoom(i.interest_id)} data-testid={`interest-invite-room-${i.interest_id}`}>
                    Invite to a Care Room
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {profileFor && <CarerProfilePreview postId={postId} interestId={profileFor} onClose={() => setProfileFor(null)} />}
    </div>
  );
}

function PostEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...emptyForm, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleEss = (v) => {
    setForm((prev) => {
      const has = prev.essential_requirements.includes(v);
      return {
        ...prev,
        essential_requirements: has
          ? prev.essential_requirements.filter((x) => x !== v)
          : [...prev.essential_requirements, v],
      };
    });
  };

  const save = async (submitAfter) => {
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      let doc;
      if (initial?.post_id) {
        const { data } = await api.patch(`/api/care-needs/${initial.post_id}`, payload);
        doc = data;
      } else {
        const { data } = await api.post("/api/care-needs", payload);
        doc = data;
      }
      if (submitAfter) {
        const { data: s } = await api.post(`/api/care-needs/${doc.post_id}/submit`);
        doc = s;
      }
      toast.success(submitAfter ? "Submitted to JCC for review." : "Saved.");
      onSaved(doc);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-base p-6 space-y-4" data-testid="cn-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Txt form={form} set={set} k="care_recipient_reference" label="Person needing care — short reference (e.g. Mrs J.S.)"/>
        <Txt form={form} set={set} k="relationship_to_recipient" label="Your relationship to them"/>
        <Txt form={form} set={set} k="location_area" label="Area (region / town — not full postcode)"/>
        <Sel form={form} set={set} k="care_type" opts={CARE_TYPES} label="Type of care"/>
        <Sel form={form} set={set} k="preferred_start_timing" opts={START_TIMINGS} label="Preferred start timing"/>
        <Txt form={form} set={set} k="start_date" label="Exact start date (optional)" type="date"/>
        <Sel form={form} set={set} k="duration_band" opts={DURATION_BANDS} label="Expected duration"/>
        <Sel form={form} set={set} k="hours_per_week_band" opts={HOURS_BANDS} label="Hours per week"/>
        <Sel form={form} set={set} k="funding_type" opts={FUNDING_TYPES} label="Funding type"/>
        <Sel form={form} set={set} k="budget_band" opts={BUDGET_BANDS} label="Budget range"/>
        <Txt form={form} set={set} k="rota_pattern" label="Rota / working pattern"/>
        <Txt form={form} set={set} k="expiry_review_date" label="Post review / expiry date (optional)" type="date"/>
      </div>
      <Area form={form} set={set} k="support_needs" label="Support needs" rows={4}/>
      <Area form={form} set={set} k="night_expectations" label="Night expectations"/>
      <div>
        <span className="text-sm font-medium">Essential requirements</span>
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ESSENTIALS.map(([v, l]) => (
            <label key={v} className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.essential_requirements.includes(v)}
                     onChange={() => toggleEss(v)}
                     data-testid={`cn-ess-${v}`}/>
              {l}
            </label>
          ))}
        </div>
      </div>
      <Area form={form} set={set} k="additional_details" label="Anything else that would help JCC and any future carer" rows={4}/>
      <PrivacyReassuranceNote heading="Your privacy matters." testId="care-needs-privacy-note">
        JCC will never sell the information you provide or use it for unrelated marketing. We only
        process it to provide and protect the JCC service. Please do not include names, exact
        addresses, medical records or other details that could identify the person receiving care in
        the publicly visible description.
      </PrivacyReassuranceNote>
      <div className="flex flex-wrap gap-3 pt-2">
        <Button disabled={busy} onClick={() => save(false)} variant="outline" data-testid="cn-save-draft">
          Save draft
        </Button>
        <Button disabled={busy} onClick={() => save(true)} data-testid="cn-submit-review">
          Submit for JCC review
        </Button>
        <Button disabled={busy} onClick={onCancel} variant="ghost">Cancel</Button>
      </div>
    </div>
  );
}

function CloseOutcomeDialog({ open, onClose, onConfirm, busy }) {
  const [outcome, setOutcome] = useState("");
  useEffect(() => { if (open) setOutcome(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cn-close-dialog">
        <DialogHeader>
          <DialogTitle>Close this Care Need?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink-muted">
          Did you find a carer through JCC? This is optional and just helps JCC understand
          whether the platform is helping people connect — it's not about the quality of any
          care arrangement.
        </p>
        <RadioGroup value={outcome} onValueChange={setOutcome} data-testid="cn-close-outcome-group">
          {CLOSE_OUTCOMES.map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
              <RadioGroupItem value={value} data-testid={`cn-close-outcome-${value}`} />
              {label}
            </label>
          ))}
        </RadioGroup>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onConfirm(null)} data-testid="cn-close-skip">
            Skip and close
          </Button>
          <Button disabled={busy || !outcome} onClick={() => onConfirm(outcome)} data-testid="cn-close-confirm">
            Close post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FamilyCareNeeds() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | {post_id?} | 'new'
  const [openInterestsPostId, setOpenInterestsPostId] = useState(null);
  const [highlightPostId, setHighlightPostId] = useState(null);
  const [closingPostId, setClosingPostId] = useState(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/care-needs/mine");
      setPosts(data.posts || []);
    } catch (e) { toast.error("Could not load your posts."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link support from the approval / carer-interest emails:
  // ?post=<id> highlights + scrolls to that post, ?open_responses=1
  // also opens its responses panel straight away.
  useEffect(() => {
    const postId = searchParams.get("post");
    if (!postId) return;
    setHighlightPostId(postId);
    if (searchParams.get("open_responses") === "1") setOpenInterestsPostId(postId);
    const el = document.querySelector(`[data-testid="cn-post-${postId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts]);

  const action = async (pid, path, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await api.post(`/api/care-needs/${pid}/${path}`);
      toast.success("Updated.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Action failed."); }
  };

  const confirmClose = async (outcome) => {
    setCloseBusy(true);
    try {
      await api.post(`/api/care-needs/${closingPostId}/close`, { outcome });
      toast.success("Post closed.");
      setClosingPostId(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not close this post."); }
    finally { setCloseBusy(false); }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this draft permanently?")) return;
    try {
      await api.delete(`/api/care-needs/${pid}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed."); }
  };

  return (
    <div data-testid="page-family-care-needs">
      <PageHero eyebrow="Family" title="Post your care needs"
                description="Draft your care needs, send them to JCC for review, and manage them privately here. JCC checks each post before sharing it with any carer."/>
      <section className="section pt-10">
        <div className="container-jcc max-w-4xl space-y-8">
          {editing ? (
            <PostEditor
              initial={editing === "new" ? {} : editing}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }}
            />
          ) : (
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-display font-semibold">My care-needs posts</h2>
              <Button onClick={() => setEditing("new")} data-testid="cn-new-btn">
                + New care-needs post
              </Button>
            </div>
          )}

          {!editing && (loading ? (
            <p>Loading…</p>
          ) : posts.length === 0 ? (
            <p className="text-ink-muted">You haven't posted any care needs yet.</p>
          ) : (
            <ul className="space-y-4" data-testid="cn-post-list">
              {posts.map((p) => (
                <li key={p.post_id} className={`card-base p-5 transition-shadow ${highlightPostId === p.post_id ? "ring-2 ring-purple shadow-lg" : ""}`} data-testid={`cn-post-${p.post_id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="font-semibold">
                        {p.care_recipient_reference || "Untitled post"}{" "}
                        {p.location_area ? <span className="text-ink-muted text-sm">• {p.location_area}</span> : null}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <StatusBadge label={p.status_label} status={p.status}/>
                        {p.interest_count > 0 && (
                          <span
                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800"
                            data-testid={`cn-interest-count-${p.post_id}`}
                          >
                            {p.interest_count} new response{p.interest_count === 1 ? "" : "s"}
                          </span>
                        )}
                        <button
                          type="button"
                          className="text-xs font-medium text-purple hover:underline"
                          onClick={() => setOpenInterestsPostId(openInterestsPostId === p.post_id ? null : p.post_id)}
                          data-testid={`cn-view-responses-${p.post_id}`}
                        >
                          {openInterestsPostId === p.post_id ? "Hide responses" : "View responses"}
                        </button>
                      </div>
                      {openInterestsPostId === p.post_id && <InterestsPanel postId={p.post_id} />}
                      {p.family_feedback_note && (p.status === "changes_requested" || p.status === "rejected") && (
                        <p className="mt-2 text-sm bg-amber-50 border border-amber-200 rounded p-2" data-testid="cn-feedback-note">
                          <strong>JCC feedback: </strong>{p.family_feedback_note}
                        </p>
                      )}
                      {p.needs_review && (
                        <p className="mt-2 text-xs text-amber-700">This post is due for a review — please refresh or close if no longer needed.</p>
                      )}
                      {p.relisted_from_post_id && (
                        <p className="mt-2 text-xs text-ink-muted">Relisted from an earlier matched arrangement.</p>
                      )}
                      {["approved", "paused", "matched"].includes(p.status) && (
                        <p className="mt-2 text-xs text-ink-muted" data-testid={`cn-manage-hint-${p.post_id}`}>
                          {p.status === "matched"
                            ? "Marked as matched — this does not close your post automatically. Close it separately once you no longer need responses, or find another carer."
                            : "Keep this post open if you're still looking for another carer. Pause it to hide it from carers temporarily, or close it once you no longer need responses."}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {["draft", "changes_requested", "approved", "paused"].includes(p.status) && (
                        <Button size="sm" variant="outline" onClick={() => setEditing(p)}
                                data-testid={`cn-edit-${p.post_id}`}>Edit</Button>
                      )}
                      {p.status === "draft" && (
                        <>
                          <Button size="sm" onClick={() => action(p.post_id, "submit")}
                                  data-testid={`cn-submit-${p.post_id}`}>Submit for review</Button>
                          <Button size="sm" variant="ghost" onClick={() => del(p.post_id)}>Delete</Button>
                        </>
                      )}
                      {p.status === "changes_requested" && (
                        <Button size="sm" onClick={() => action(p.post_id, "submit")}>Resubmit</Button>
                      )}
                      {p.status === "approved" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => action(p.post_id, "pause")} data-testid={`cn-pause-${p.post_id}`}>Pause</Button>
                          <Button size="sm" variant="outline" onClick={() => action(p.post_id, "matched", "Mark this post as matched? It will become read-only, but stays open — you'll close it separately when ready.")} data-testid={`cn-matched-${p.post_id}`}>Mark as matched</Button>
                          <Button size="sm" variant="ghost" onClick={() => setClosingPostId(p.post_id)} data-testid={`cn-close-${p.post_id}`}>Close</Button>
                        </>
                      )}
                      {p.status === "paused" && (
                        <>
                          <Button size="sm" onClick={() => action(p.post_id, "resume")} data-testid={`cn-resume-${p.post_id}`}>Resume</Button>
                          <Button size="sm" variant="ghost" onClick={() => setClosingPostId(p.post_id)} data-testid={`cn-close-${p.post_id}`}>Close</Button>
                        </>
                      )}
                      {p.status === "matched" && (
                        <>
                          <Button size="sm" onClick={() => action(p.post_id, "relist", "Create a fresh draft for another carer? The current matched post will be kept for history.")}
                                  data-testid={`cn-relist-${p.post_id}`}>Find another carer</Button>
                          <Button size="sm" variant="ghost" onClick={() => setClosingPostId(p.post_id)} data-testid={`cn-close-${p.post_id}`}>Close</Button>
                        </>
                      )}
                      {p.status === "pending_review" && (
                        <span className="text-xs text-ink-muted">Awaiting JCC review</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </section>
      <CloseOutcomeDialog
        open={!!closingPostId}
        onClose={() => setClosingPostId(null)}
        onConfirm={confirmClose}
        busy={closeBusy}
      />
    </div>
  );
}
